import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { NgClass } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import {
	CityModalComponent,
	CityModalData,
} from '@/new-transport/_shared/city-map/city/city-modal/city-modal.component';
import { MatMenuModule } from '@angular/material/menu';
import { StopKind, TransportSide, VehicleTransport } from '@/new-transport/_models/transport.model';
import { distinctUntilChanged, filter, map } from 'rxjs';
import { TransportMapState, TransportMapStore } from '@/new-transport/transport-map/_store/transport-map.store';
import { City, Program, Slot } from '@/new-transport/_models/transport-map.model';
import { mergeMaps } from '@/_utils/merge-two-maps';
import { programFactory } from '@/new-transport/_factory/program-factory';
import { getState } from '@ngrx/signals';
import { Programs } from '@/new-transport/_shared/city-map/city/city-modal/participants-tab/participants-tab.model';
import { NewUndoRedoService } from '@/common/undo-redo/new-undo-redo.service';
import { deepClone } from '@/_utils/deep-clone';
import { removeDuplicatesFromArray } from '@/_utils/remove-duplicates-from-array';
import { calculateParticipantsAmountFromPrograms } from '@/new-transport/_utils/calculate-participants-amount-from-programs/calculate-participants-amount-from-programs';
import { ActivatedRoute } from '@angular/router';
import { OfferTransportKind } from '@/common/enums/OfferTransportKind';
import { toSignal } from '@angular/core/rxjs-interop';
import { InfoboxService } from '@/common/infobox/infobox.service';

type ProgramsToCheck = 'taken' | 'outside';

@Component({
	selector: 'app-city',
	standalone: true,
	imports: [MatMenuModule, NgClass],
	templateUrl: './city.component.html',
	styleUrl: './city.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CityComponent {
	readonly city = input.required<City>();
	readonly vehicleInUse = input.required<VehicleTransport>();

	readonly passThroughSelected = output<number>();

	readonly isCityWithSell = computed(() => this.sumOfPeople().confirm > 0 || this.sumOfPeople().option > 0);

	readonly sumOfPeople = computed(() =>
		this.city()?.programsFromOfferTransport.reduce(
			(acc, value) => {
				acc.confirm += value.confirmedParticipants;
				acc.option += value.optionParticipants;
				return acc;
			},
			{ confirm: 0, option: 0 },
		),
	);

	alreadyTakenSlotsGroupByVehicle = computed(() => {
		return this.city().alreadyTakenSlotsByVehicle.map((value) => {
			return {
				vehicle: this.store.vehicles().find((v) => v.id === value.vehicleId),
				alreadyTakenSlots: value.alreadyTakenSlots,
			};
		});
	});

	totalAlreadyTakenSlots = computed(() => {
		return this.alreadyTakenSlotsGroupByVehicle().reduce((sum, current) => sum + current.alreadyTakenSlots, 0);
	});

	allPeopleIsTaken = computed(() => {
		return (
			this.totalAlreadyTakenSlots() === this.sumOfPeople().confirm + this.sumOfPeople().option &&
			this.city().antennaParticipants === 0
		);
	});

	vehiclesWhoseTakenSlots = computed(() => {
		return this.alreadyTakenSlotsGroupByVehicle().map((v) => v.vehicle.name);
	});

	readonly programsToTake = computed(() => {
		const filteredSlotsFromOfferTransport = this.excludeSlotsIncludedInGivenPrograms(
			this.city().programsFromOfferTransport,
			'outside',
		);

		const programsOutsideVehicle = this.mergePrograms(
			this.city().programsOutsideVehicle.concat(filteredSlotsFromOfferTransport),
		);

		const programs = this.excludeSlotsIncludedInGivenPrograms(programsOutsideVehicle, 'taken');

		const programsWithoutDuplicatedSlots = this.removeDuplicatesFromSlots(programs);

		return this.removeEmptyFieldsFromPrograms(programsWithoutDuplicatedSlots);
	});

	readonly transportKind = toSignal<OfferTransportKind>(
		inject(ActivatedRoute).queryParams.pipe(
			map((params) => params?.transportKind ?? OfferTransportKind.COACH),
			distinctUntilChanged(),
		),
	);

	readonly OfferTransportKind = OfferTransportKind;

	readonly store = inject(TransportMapStore);

	private readonly undoRedoService = inject(NewUndoRedoService<TransportMapState>);
	private readonly dialog = inject(MatDialog);
	private readonly infoboxService = inject(InfoboxService);

	openCityModal(): void {
		const startProgramsToTake = deepClone(this.programsToTake());
		const startProgramsInRoute = deepClone(this.store.takenPrograms().get(this.vehicleInUse()?.id)) || [];
		this.dialog
			.open<CityModalComponent, CityModalData>(CityModalComponent, {
				data: {
					currentCity: this.city(),
					programs: this.programsToTake(),
					programsInRoute: deepClone(this.store.takenPrograms().get(this.vehicleInUse()?.id)) || [],
					vehicleInUse: this.vehicleInUse(),
					vehicleInUseFreeSeats: this.store.vehicleInUseFreeSeats(),
				},
				disableClose: true,
				minWidth: '60%',
			})
			.afterClosed()
			.pipe(filter((v) => !!v))
			.subscribe((result: Programs) => {
				const filteredOutsideVehicle = this.filterProgramsByExcludedSlots(
					result.outsideVehicle,
					startProgramsToTake,
				);

				const filteredTaken = this.filterProgramsByExcludedSlots(result.taken, startProgramsInRoute);

				const groupedOutsideVehiclePrograms = this.groupProgramsByCity(filteredOutsideVehicle);
				this.setVehicleRoutesForPrograms(groupedOutsideVehiclePrograms, StopKind.DROP_OFF);

				this.setProgramOutsideVehicle(result.outsideVehicle.length === 0 ? [] : result.outsideVehicle);

				if (result.taken.length === 0) {
					this.store.clearTakenProgramsForVehicle(this.vehicleInUse().id);
				} else {
					this.store.appendProgramToTakenPrograms(result.taken, this.vehicleInUse().id, true);
				}

				const participantsAmount = calculateParticipantsAmountFromPrograms(result.taken);
				this.store.setVehicleInUseTakenSeats(participantsAmount);

				const groupedTakenPrograms = this.groupProgramsByCity(filteredTaken);
				this.setVehicleRoutesForPrograms(groupedTakenPrograms, StopKind.BOARDING);

				this.undoRedoService.setState(deepClone(getState(this.store)));
			});
	}

	takeAll(): void {
		const participantsAmount = calculateParticipantsAmountFromPrograms(this.programsToTake());
		const alreadyTakenAmount = calculateParticipantsAmountFromPrograms(
			this.store.takenPrograms().get(this.vehicleInUse().id) || [],
		);
		console.log(this.transportKind());

		if (this.transportKind() === OfferTransportKind.PLANE) {
			this.planeValidation();
		}

		if (participantsAmount > this.store.vehicleInUseFreeSeats()) {
			this.infoboxService.error('Liczba uczestników jest większa niż ilość wolnych miejsc w pojeździe');
			return;
		}

		this.store.setVehicleInUseTakenSeats(participantsAmount + alreadyTakenAmount);

		this.setProgramOutsideVehicle([]);

		this.takePrograms(
			this.programsToTake().length === 0
				? this.createEmptyProgram()
				: this.groupProgramsByCity(this.programsToTake()),
		);

		this.undoRedoService.setState(deepClone(getState(this.store)));
	}

	leftAll(): void {
		const takenPrograms = this.store
			.takenPrograms()
			.get(this.vehicleInUse().id)
			?.filter((v) => v.fromCity !== this.city().id);

		if (this.isAlreadyEmptyActionCreated(takenPrograms)) {
			return;
		}

		this.setVehicleRoutesForPrograms(
			takenPrograms?.length === 0 ? this.createEmptyProgram() : takenPrograms,
			StopKind.DROP_OFF,
		);

		this.store.setProgramOutsideVehicle({
			programs: this.mergePrograms(this.city().programsOutsideVehicle.concat(takenPrograms)),
			cityId: this.city().id,
			vehicleId: this.vehicleInUse().id,
		});

		this.store.clearTakenProgramsForVehicle(this.vehicleInUse().id);
		this.store.releaseAllVehicleInUseTakenSeats();
		this.undoRedoService.setState(deepClone(getState(this.store)));
	}

	passThrough(): void {
		// TODO: Write that down in documentation. We use "(From | Origin) city" term to describe the original city where the program was taken. There are scenarios where for example we take all people from Poznań and them leave them in Wrocław. In this case, the "(From | Origin) city" is Poznań.
		this.setVehicleRoute(StopKind.CRUISING, this.store.destinations()[0].id, this.city().id);
		this.undoRedoService.setState(deepClone(getState(this.store)));
	}

	private mergePrograms(programs: Program[]): Program[] {
		return programs.reduce((acc, program) => {
			const alreadyExists = acc.some((v) => v.destinationId === program.destinationId);
			if (alreadyExists) {
				acc = acc.map((programToLeave) => {
					if (programToLeave.destinationId !== program.destinationId) {
						return programToLeave;
					}

					programToLeave.reservation = mergeMaps<number, Slot[]>(
						programToLeave.reservation,
						program.reservation,
					);
					return programToLeave;
				});
			} else {
				const map = new Map(program.reservation);
				acc = [...acc, programFactory(program.destinationId, program.destinationName, map, program.fromCity)];
			}
			return acc;
		}, []);
	}

	private filterProgramsByExcludedSlots(programsToFilter: Program[], exclusionPrograms: Program[]): Program[] {
		if (!exclusionPrograms) {
			return programsToFilter;
		}

		const exclusionSlotIds = new Set<number>();
		exclusionPrograms.forEach((program) => {
			program.reservation.forEach((slots) => {
				slots.forEach((slot) => exclusionSlotIds.add(slot.id));
			});
		});

		const filteredPrograms = programsToFilter.map((program) => {
			const updatedReservations = new Map<number, Slot[]>();

			program.reservation.forEach((slots, key) => {
				const remainingSlots = slots.filter((slot) => !exclusionSlotIds.has(slot.id));
				if (remainingSlots.length > 0) {
					updatedReservations.set(key, remainingSlots);
				}
			});

			return {
				...program,
				reservation: updatedReservations,
			};
		});

		return filteredPrograms.filter((program) => program.reservation.size > 0);
	}

	private removeEmptyFieldsFromPrograms(programs: Program[]): Program[] {
		return programs
			.map((program) => {
				const newProgram = { ...program };
				newProgram.reservation = new Map(
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
					this.getSlotsFromPrograms(newProgram).filter(([_, value]) => value.length > 0),
				);
				return newProgram;
			})
			.filter((program) => program.reservation.size > 0);
	}

	private excludeSlotsIncludedInGivenPrograms(
		programsToExclude: Program[],
		programsToCheck: ProgramsToCheck,
	): Program[] {
		return programsToExclude.map((program) => {
			const filteredReservations = new Map(
				this.getSlotsFromPrograms(program).map(([key, slots]) => {
					const filteredSlots = slots.filter((slot) => !this.isSlotInPrograms(slot.id, programsToCheck));
					return [key, filteredSlots];
				}),
			);

			return {
				...program,
				reservation: filteredReservations,
			};
		});
	}

	private isSlotInPrograms(slotId: number, fromPrograms: ProgramsToCheck): boolean {
		const programs =
			fromPrograms === 'taken'
				? Array.from(this.store.takenPrograms().values()).flatMap((arr) => arr) || []
				: this.store
						.programsOutsideVehicle()
						.filter((program) => program.cityId !== this.city().id)
						.flatMap((v) => v.programs);

		const allSlotsFromPrograms = programs.reduce(
			(acc, program) => acc.concat(Array.from(program.reservation.values()).flat()),
			[],
		);

		const slotIncluded = allSlotsFromPrograms.some((v) => v.id === slotId);
		return slotIncluded;
	}

	private getSlotsFromPrograms(program: Program): [number, Slot[]][] {
		return Array.from(program.reservation.entries());
	}

	private takePrograms(programs: Program[]): void {
		this.store.appendProgramToTakenPrograms(this.programsToTake(), this.vehicleInUse().id, false);
		this.setVehicleRoutesForPrograms(programs, StopKind.BOARDING);
	}

	private removeDuplicatesFromSlots(programs: Program[]): Program[] {
		return programs.map((program) => {
			const filteredReservations = new Map(
				this.getSlotsFromPrograms(program).map(([key, slots]) => {
					const filteredSlots = removeDuplicatesFromArray(slots);
					return [key, filteredSlots];
				}),
			);
			return {
				...program,
				reservation: filteredReservations,
			};
		});
	}

	private groupProgramsByCity(programs: Program[]): Program[] {
		return programs.flatMap((program) => {
			const fromCityMap = new Map<number, { slots: Slot[]; reservationId: number }>();

			this.getSlotsFromPrograms(program).forEach(([key, slots]) => {
				slots.forEach((slot) => {
					const stopSide =
						this.store.transport().side === TransportSide.DEPARTURE ? 'stopDeparture' : 'stopReturn';

					const cityId = slot[stopSide].stop.id;

					if (fromCityMap.has(cityId)) {
						fromCityMap.get(cityId).slots.push(slot);
					} else {
						fromCityMap.set(cityId, {
							slots: [slot],
							reservationId: key,
						});
					}
				});
			});

			return Array.from(fromCityMap.entries()).map(([cityId, { slots, reservationId }]) => {
				const myMap = new Map().set(reservationId, slots);
				return programFactory(program.destinationId, program.destinationName, myMap, cityId);
			});
		});
	}

	private setProgramOutsideVehicle(programs: Program[]): void {
		this.store.setProgramOutsideVehicle({
			programs,
			cityId: this.city().id,
			vehicleId: this.vehicleInUse().id,
		});
	}

	private setVehicleRoute(
		action: StopKind,
		destinationId: number,
		fromCity: number,
		reservations?: Map<number, Slot[]>,
	): void {
		this.store.setVehicleRoute({
			action,
			cityId: this.city().id,
			vehicleId: this.vehicleInUse().id,
			destinationId,
			slots: reservations ? Array.from(reservations.values()).flat() : [],
			fromCity,
		});
	}

	private setVehicleRoutesForPrograms(programs: Program[], action: StopKind): void {
		if (!programs) {
			return;
		}

		programs.forEach((program) => {
			const { destinationId, fromCity, reservation } = program;
			this.setVehicleRoute(action, destinationId, fromCity, reservation);
		});
	}

	private takenProgramsFromOfferTransport(programs: Programs): Array<Program & { reservation: Map<number, Slot[]> }> {
		const takenProgramsSlots = programs.taken.flatMap((value) => {
			const slotsEntries = this.getSlotsFromPrograms(value);
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const slotsFromPrograms: Slot[] = slotsEntries.flatMap(([_, slots]) => slots);
			return slotsFromPrograms.map((slot) => slot.id);
		});

		return deepClone(
			this.city().programsFromOfferTransport.map((program) => {
				const filteredReservation: Map<number, Slot[]> = new Map();

				program.reservation.forEach((slots, key) => {
					filteredReservation.set(
						key,
						slots.filter((item) => takenProgramsSlots.includes(item.id)).filter(Boolean),
					);
				});

				return {
					...program,
					reservation: filteredReservation,
				};
			}),
		);
	}

	private createEmptyProgram(): Program[] {
		return this.store.destinations().map((v) => programFactory(v.id, v.address, new Map(), this.city().id));
	}

	private isAlreadyEmptyActionCreated(takenPrograms: Program[]): boolean {
		return !Array.isArray(takenPrograms);
	}

	private planeValidation(): void {
		const routeForVehicles = this.store.routeForVehicles();
		const currentVehicleId = this.vehicleInUse().id;
		const selectedCityId = this.city().id;

		if (routeForVehicles.has(currentVehicleId)) {
			this.infoboxService.error(
				'Jeden samolot może mieć przypisane tylko jedno miasto. <br> Wybierz kolejny samolot, jeśli chcesz dodać więcej miast.',
			);
			return;
		}

		const isCityAlreadyAssigned = [...routeForVehicles.values()]
			.flat()
			.some(({ cityId }) => cityId === selectedCityId);

		if (isCityAlreadyAssigned) {
			this.infoboxService.error('Wybrane miasto jest już przypisane do innego samolotu.');
			return;
		}
	}
}
